import React, { useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton } from '@/components/brand/Buttons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fadeUp, viewportOnce } from '@/lib/motion';
import { HOME } from '@/constants/testIds';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

/* ── Inline SVG brand marks ── */
function OpenAIIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.51-2.9 6.07 6.07 0 0 0-10.78 2.9 5.98 5.98 0 0 0-3.99 2.9 6.05 6.05 0 0 0 .74 7.1 5.98 5.98 0 0 0 .51 4.91 6.05 6.05 0 0 0 6.51 2.9A6.07 6.07 0 0 0 13.26 24a6.06 6.06 0 0 0 5.77-4.21 5.99 5.99 0 0 0 3.99-2.9 6.06 6.06 0 0 0-.74-7.07zM13.26 22.49a4.48 4.48 0 0 1-2.88-1.04l.14-.08 4.78-2.76a.79.79 0 0 0 .39-.68V11.6l2.02 1.17a.07.07 0 0 1 .04.05v5.58a4.5 4.5 0 0 1-4.49 4.49zM3.6 18.36a4.47 4.47 0 0 1-.53-3.01l.14.08 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.33a.08.08 0 0 1-.03.06L9.74 19.95A4.5 4.5 0 0 1 3.6 18.36zM2.34 7.9a4.48 4.48 0 0 1 2.37-1.97v5.67a.77.77 0 0 0 .39.68l5.81 3.35-2.02 1.17a.08.08 0 0 1-.07 0L3.65 13.9A4.5 4.5 0 0 1 2.34 7.9zm16.6 3.86-5.84-3.36L15.12 7.2a.08.08 0 0 1 .07 0l4.83 2.79a4.49 4.49 0 0 1-.68 8.1V12.44a.79.79 0 0 0-.4-.67zm2.01-3.02-.14-.09-4.77-2.78a.78.78 0 0 0-.79 0L9.41 9.23V6.9a.07.07 0 0 1 .03-.06l4.83-2.79a4.5 4.5 0 0 1 6.68 4.66zM8.31 12.86l-2.02-1.16a.08.08 0 0 1-.04-.06V6.07a4.5 4.5 0 0 1 7.38-3.45l-.14.08-4.78 2.76a.79.79 0 0 0-.39.68zm1.1-2.37 2.6-1.5 2.61 1.5v2.99l-2.6 1.5-2.61-1.5z" />
    </svg>
  );
}
function AnthropicIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M13.83 3h-3.66L4 21h3.3l1.3-3.6h6.8l1.3 3.6H20L13.83 3zM9.5 14.4l2.5-7 2.5 7H9.5z" />
    </svg>
  );
}
function GeminiIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.69 7.31 6.69 7.31 2 12c4.69 5.31 4.69 5.31 10 10 5.31-4.69 5.31-4.69 10-10C17.31 7.31 17.31 7.31 12 2z" opacity=".9"/>
      <path d="M12 5.5c0 3.59-2.91 6.5-6.5 6.5C9.09 12 12 14.91 12 18.5c0-3.59 2.91-6.5 6.5-6.5C14.91 12 12 9.09 12 5.5z" fill="white" opacity=".7"/>
    </svg>
  );
}
function MicrosoftIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M2 2h9.5v9.5H2z" fill="#F25022"/>
      <path d="M12.5 2H22v9.5h-9.5z" fill="#7FBA00"/>
      <path d="M2 12.5h9.5V22H2z" fill="#00A4EF"/>
      <path d="M12.5 12.5H22V22h-9.5z" fill="#FFB900"/>
    </svg>
  );
}
function AWSIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6.76 10.45c0 .28.03.5.09.66.06.16.15.33.27.51a.32.32 0 0 1 .05.17c0 .07-.04.15-.14.22l-.44.3a.35.35 0 0 1-.19.07c-.07 0-.15-.04-.22-.11a2.3 2.3 0 0 1-.27-.35 6.2 6.2 0 0 1-.23-.44c-.58.68-1.3 1.02-2.17 1.02-.62 0-1.12-.18-1.48-.53-.36-.35-.54-.82-.54-1.4 0-.62.22-1.12.66-1.49.44-.37 1.02-.56 1.76-.56.24 0 .5.02.77.06.27.04.54.1.83.17v-.53c0-.55-.12-.93-.35-1.16-.23-.23-.63-.34-1.2-.34-.26 0-.52.03-.79.1-.27.06-.53.15-.79.26a2.1 2.1 0 0 1-.26.1.46.46 0 0 1-.12.02c-.1 0-.16-.08-.16-.23v-.36c0-.12.02-.21.05-.26a.55.55 0 0 1 .21-.16c.26-.13.57-.24.93-.33.36-.09.74-.13 1.15-.13.87 0 1.51.2 1.92.6.4.4.6.99.6 1.79v2.36zm-2.99.73c.24 0 .48-.04.74-.13.26-.09.49-.26.68-.49.11-.13.19-.28.24-.45.04-.17.07-.37.07-.61v-.3a6.08 6.08 0 0 0-.67-.1 5.35 5.35 0 0 0-.68-.04c-.49 0-.84.1-1.08.29-.24.19-.36.47-.36.83 0 .34.09.59.26.77.17.18.42.23.8.23zm5.83.78c-.14 0-.23-.02-.29-.08-.06-.05-.12-.16-.17-.31l-1.88-6.19a1.4 1.4 0 0 1-.07-.33c0-.13.07-.2.2-.2h.8c.14 0 .24.02.29.08.06.05.11.16.16.31l1.34 5.29 1.25-5.29c.04-.16.09-.26.15-.31.06-.05.17-.08.3-.08h.66c.14 0 .24.02.3.08.06.05.12.16.16.31l1.26 5.36 1.38-5.36c.05-.16.1-.26.16-.31.06-.05.16-.08.29-.08h.77c.13 0 .2.07.2.2 0 .04-.01.08-.02.13l-.05.2-1.93 6.19c-.05.16-.1.26-.16.31-.07.05-.17.08-.3.08h-.7c-.14 0-.24-.02-.3-.08-.06-.05-.12-.16-.16-.32L11.4 6.1l-1.24 5.25c-.04.16-.1.27-.16.32-.06.05-.17.08-.3.08zm10.39.16c-.43 0-.85-.05-1.26-.15-.41-.1-.73-.21-.94-.34-.13-.07-.22-.16-.25-.24a.6.6 0 0 1-.04-.23v-.38c0-.15.06-.22.17-.22.04 0 .09.01.13.02l.18.08c.24.11.5.2.78.26.28.06.56.09.84.09.45 0 .79-.08 1.03-.24.24-.16.37-.4.37-.7a.62.62 0 0 0-.17-.44c-.11-.12-.33-.23-.64-.33L18 9.13c-.48-.15-.84-.38-1.06-.67a1.56 1.56 0 0 1-.34-.97c0-.28.06-.53.18-.74.12-.21.28-.4.49-.54.2-.15.44-.26.71-.34.27-.08.55-.11.84-.11.15 0 .29.01.44.03l.42.07c.13.03.26.07.38.11.12.04.22.09.3.13.1.05.18.11.22.18a.44.44 0 0 1 .06.23v.35c0 .15-.06.22-.17.22a.77.77 0 0 1-.28-.1 3.38 3.38 0 0 0-1.38-.28c-.4 0-.72.07-.94.2-.22.13-.34.34-.34.62 0 .17.06.32.19.44.13.12.36.24.7.34l.95.3c.48.15.82.37 1.03.65.21.28.31.6.31.95 0 .29-.06.55-.18.78-.12.23-.29.43-.5.59-.22.17-.47.29-.77.38-.3.1-.63.14-.98.14z" />
      <path d="M20.16 16.26c-2.44 1.8-5.97 2.76-9.01 2.76-4.26 0-8.09-1.57-10.99-4.19-.23-.2-.02-.48.25-.32 3.13 1.82 7 2.91 10.99 2.91 2.7 0 5.66-.56 8.39-1.72.41-.18.75.27.37.56z" fill="#FF9900"/>
      <path d="M21.15 15.13c-.31-.4-2.06-.19-2.84-.1-.24.03-.27-.18-.06-.33 1.39-.98 3.68-.7 3.94-.37.27.33-.07 2.61-1.38 3.7-.2.17-.39.08-.3-.14.3-.73.96-2.37.64-2.76z" fill="#FF9900"/>
    </svg>
  );
}
function MetaIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973.14.603.35 1.144.618 1.566.516.81 1.25 1.292 2.007 1.292.74 0 1.428-.395 2.375-1.612.606-.787 1.158-1.693 1.761-2.697l.32-.52c.1-.162.199-.324.3-.484.068-.108.135-.216.2-.323l.172-.278c.063-.1.125-.2.185-.302l.153-.252.16-.266.167-.278c.048-.08.098-.16.148-.24l.173-.284.186-.304.173-.282a78.58 78.58 0 0 1 .345-.562l.18-.29a78.623 78.623 0 0 1 1.674-2.502l.175-.245.175-.242.183-.252.183-.25.188-.255.188-.253.196-.261.197-.26.204-.268.205-.267.218-.28.217-.278.23-.293.23-.29.245-.308.244-.305.26-.323.259-.32.275-.338.274-.336.29-.353.289-.35.307-.37.307-.368.326-.387.325-.386.347-.408.346-.406.37-.43.37-.429.396-.455.396-.453.424-.48.424-.479.457-.51.456-.508.492-.544.491-.54.531-.579.53-.577.57-.617.57-.615.614-.657.612-.653.66-.698.657-.694a.83.83 0 0 0-.033-1.173.823.823 0 0 0-1.172.033z"/>
    </svg>
  );
}
function MistralIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <rect x="2" y="2" width="5" height="5"/>
      <rect x="9" y="2" width="5" height="5"/>
      <rect x="16" y="2" width="5" height="5"/>
      <rect x="2" y="9" width="5" height="5"/>
      <rect x="16" y="9" width="5" height="5"/>
      <rect x="9" y="16" width="5" height="5"/>
      <rect x="16" y="16" width="5" height="5"/>
    </svg>
  );
}
function CohereIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="5.5" fill="none" stroke="white" strokeWidth="2"/>
      <circle cx="12" cy="12" r="2" fill="white"/>
    </svg>
  );
}
function PerplexityIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
    </svg>
  );
}

const AI_LOGO_MAP = {
  'OpenAI': OpenAIIcon,
  'Claude': AnthropicIcon,
  'Gemini': GeminiIcon,
  'Copilot': MicrosoftIcon,
  'AWS Bedrock': AWSIcon,
  'Llama 3.3': MetaIcon,
  'Mistral': MistralIcon,
  'Cohere': CohereIcon,
  'Perplexity': PerplexityIcon,
};

const EMPTY = { name: '', email: '', country: 'us', phone: '', company: '', brief: '' };

export default function CtaBand({ data }) {
  const [form, setForm]     = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  if (!data) return null;

  const aiPartners = (data?.ai_partners || []).map((p) => ({
    ...p,
    Logo: AI_LOGO_MAP[p.name] || p.Logo || null,
  }));

  const formTitle = data?.form_title;
  const reassurance = data?.reassurance;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/contact`, {
        name:    form.name,
        email:   form.email,
        phone:   form.phone   || undefined,
        country: form.country || undefined,
        company: form.company || undefined,
        message: form.brief   || undefined,
      });
      toast.success("Message sent! We'll reach out within one business day.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="cta"
      className="py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(120deg, #1F2D45 0%, #2F6FB0 45%, #C9962E 100%)' }}
    >
      {/* Dot pattern */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.08]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="ctadots" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.2" fill="#fff" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#ctadots)" />
      </svg>

      <div className="container mx-auto grid lg:grid-cols-2 gap-10 items-center relative">

        {/* ── Left: headline + AI partner cards ── */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Gain An Upper Edge
            <br />Over Your Competitors
            <br />With <span className="text-[#FFD988]">AI-Driven Development</span>
          </h2>

          {/* AI partner chips */}
          <div className="mt-8 flex flex-wrap gap-2">
            {aiPartners.map((p) => {
              const Logo = p.Logo;
              return (
                <div
                  key={p.name}
                  className="group flex items-center gap-2 rounded-full px-3 py-1.5 border transition-all duration-300 cursor-default"
                  style={{
                    background: `${p.color}18`,
                    borderColor: `${p.color}55`,
                    boxShadow: `0 0 0 0 ${p.color}00`,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = `${p.color}30`;
                    e.currentTarget.style.borderColor = `${p.color}cc`;
                    e.currentTarget.style.boxShadow = `0 0 12px 2px ${p.color}40`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = `${p.color}18`;
                    e.currentTarget.style.borderColor = `${p.color}55`;
                    e.currentTarget.style.boxShadow = `0 0 0 0 ${p.color}00`;
                  }}
                >
                  <span
                    className="flex-shrink-0 flex items-center justify-center rounded-full h-6 w-6"
                    style={{ background: `${p.color}30`, color: p.color }}
                  >
                    {Logo && <Logo size={14} />}
                  </span>
                  <span className="text-[12px] font-semibold text-white whitespace-nowrap">{p.name}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-7">
            <PrimaryButton
              icon={<ArrowRight className="h-4 w-4" />}
              onClick={() => {
                const first = formRef.current?.querySelector('input,textarea');
                if (first) {
                  first.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  setTimeout(() => first.focus(), 350);
                }
              }}
            >
              Let's Connect
            </PrimaryButton>
          </div>
        </motion.div>

        {/* ── Right: contact form ── */}
        <motion.form
          ref={formRef}
          data-testid={HOME.ctaForm}
          onSubmit={onSubmit}
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="rounded-2xl bg-white p-7 shadow-soft border border-white/30"
        >
          <h3 className="text-xl font-bold text-navy-deep">{formTitle}</h3>

          <div className="mt-5 grid gap-4">
            <div>
              <Label htmlFor="cta-name" className="text-xs text-slatebody">Full Name</Label>
              <Input id="cta-name" placeholder="John Doe" required value={form.name}
                onChange={(e) => set('name', e.target.value)} className="mt-1 rounded-lg border-hairline" />
            </div>
            <div>
              <Label htmlFor="cta-email" className="text-xs text-slatebody">Business Email</Label>
              <Input id="cta-email" type="email" placeholder="john@company.com" required value={form.email}
                onChange={(e) => set('email', e.target.value)} className="mt-1 rounded-lg border-hairline" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slatebody">Country</Label>
                <Select value={form.country} onValueChange={(v) => set('country', v)}>
                  <SelectTrigger className="mt-1 rounded-lg border-hairline"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">🇮🇳 India</SelectItem>
                    <SelectItem value="us">🇺🇸 USA</SelectItem>
                    <SelectItem value="uk">🇬🇧 UK</SelectItem>
                    <SelectItem value="ae">🇦🇪 UAE</SelectItem>
                    <SelectItem value="de">🇩🇪 Germany</SelectItem>
                    <SelectItem value="au">🇦🇺 Australia</SelectItem>
                    <SelectItem value="ca">🇨🇦 Canada</SelectItem>
                    <SelectItem value="sg">🇸🇬 Singapore</SelectItem>
                    <SelectItem value="other">🌍 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label htmlFor="cta-phone" className="text-xs text-slatebody">Phone Number</Label>
                <Input id="cta-phone" placeholder="+91 77952 87075" value={form.phone}
                  onChange={(e) => set('phone', e.target.value)} className="mt-1 rounded-lg border-hairline" />
              </div>
            </div>
            <div>
              <Label htmlFor="cta-company" className="text-xs text-slatebody">Company</Label>
              <Input id="cta-company" placeholder="Acme Corp." value={form.company}
                onChange={(e) => set('company', e.target.value)} className="mt-1 rounded-lg border-hairline" />
            </div>
            <div>
              <Label htmlFor="cta-brief" className="text-xs text-slatebody">Requirement Brief</Label>
              <Textarea id="cta-brief" placeholder="Tell us about your project or AI requirement…" rows={3}
                value={form.brief} onChange={(e) => set('brief', e.target.value)}
                className="mt-1 rounded-lg border-hairline resize-none" />
            </div>
          </div>

          <div className="mt-5">
            <PrimaryButton
              data-testid={HOME.ctaSubmit}
              type="submit"
              disabled={loading}
              className="w-full"
              icon={loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            >
              {loading ? 'Sending…' : 'Get Started'}
            </PrimaryButton>
          </div>

          <p className="mt-3 text-[11px] text-slatebody text-center">{reassurance}</p>
        </motion.form>
      </div>
    </section>
  );
}
